// LeadDetailsModal.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectUser, setUser } from '../../store/authSlice';
import API from '../../api';
import { toast } from 'react-toastify';
import '../css/LeadDetailsModal.css';

const EST_TZ = 'America/New_York';

function toDateYYYYMMDD(v) {
  if (!v) return '';
  try {
    const d = v instanceof Date ? v : new Date(v);
    if (isNaN(d)) return '';
    const tzOffset = d.getTimezoneOffset() * 60000;
    const local = new Date(d.getTime() - tzOffset);
    return local.toISOString().slice(0, 10);
  } catch {
    return '';
  }
}

function toHHMM(v) {
  if (!v) return '';
  if (typeof v === 'string') {
    const [hh = '00', mm = '00'] = v.split(':');
    return `${hh.padStart(2, '0')}:${mm.padStart(2, '0')}`;
  }
  try {
    const d = v instanceof Date ? v : new Date(v);
    if (isNaN(d)) return '';
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  } catch {
    return '';
  }
}

function getAny(obj, keys, fallback = '') {
  for (const k of keys) {
    if (obj && obj[k] != null && obj[k] !== '') return obj[k];
  }
  return fallback;
}

function fmtDateTime(d) {
  try {
    let val = d;
    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(val)) {
      // treat MySQL "YYYY-MM-DD HH:mm:ss" as UTC
      val = val.replace(' ', 'T') + 'Z';
    }
    const nd = new Date(val);
    if (isNaN(nd)) return '';
    return new Intl.DateTimeFormat('en-US', {
      timeZone: EST_TZ,
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }).format(nd);
  } catch {
    return '';
  }
}


function fmtDatePretty(yyyymmdd) {
  if (!yyyymmdd) return '—';
  const d = new Date(yyyymmdd);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

function initialFromName(name) {
  const s = String(name || '').trim();
  if (!s) return '•';
  return s[0].toUpperCase();
}

/** Pull { name } from JWT in local/session storage */
function getUserFromToken() {
  const tok = window.localStorage.getItem('token') || window.sessionStorage.getItem('token');
  if (!tok) return { name: '' };
  const parts = tok.split('.');
  if (parts.length < 2) return { name: '' };
  try {
    const json = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    const name =
      json?.user?.name ||
      json?.name ||
      json?.username ||
      json?.preferred_username ||
      '';
    return { name: String(name || '') };
  } catch {
    return { name: '' };
  }
}

/** EST helpers (no DST headaches for validation because we compare EST "keys") */
function estNowParts() {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: EST_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  const parts = dtf.formatToParts(new Date());
  const get = (type) => parts.find((p) => p.type === type)?.value || '';
  return {
    yyyy: get('year'),
    mm: get('month'),
    dd: get('day'),
    hh: get('hour'),
    min: get('minute'),
  };
}

function estTodayYYYYMMDD() {
  const { yyyy, mm, dd } = estNowParts();
  return `${yyyy}-${mm}-${dd}`;
}

function estNowHHMM() {
  const { hh, min } = estNowParts();
  return `${hh}:${min}`;
}

function estNowPretty() {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: EST_TZ,
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(new Date());
}

/** Convert "YYYY-MM-DD" + "HH:mm" to a comparable numeric key in EST local terms */
function makeEstKey(dateYYYYMMDD, timeHHMM) {
  if (!dateYYYYMMDD || !timeHHMM) return null;
  const d = String(dateYYYYMMDD).trim(); // YYYY-MM-DD
  const t = String(timeHHMM).trim(); // HH:mm
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return null;
  if (!/^\d{2}:\d{2}$/.test(t)) return null;
  // "YYYYMMDDHHmm" as number-ish string
  return `${d.replaceAll('-', '')}${t.replaceAll(':', '')}`;
}

function applyAmPmLogic(hhmm) {
  if (!hhmm) return hhmm;
  const [hhStr, mm] = hhmm.split(':');
  const hh = parseInt(hhStr, 10);
  if (hh >= 12) return hhmm;       // 12:00+ → already PM
  if (hh >= 10) return hhmm;       // 10:00–11:59 → AM, keep as is
  // hh < 10 → PM, convert to 24h PM equivalent
  return `${String(hh + 12).padStart(2, '0')}:${mm}`;
}

function addMinutesHHMM(hhmm, addMin) {
  if (!/^\d{2}:\d{2}$/.test(hhmm)) return '';
  const [hhStr, mmStr] = hhmm.split(':');
  let hh = Number(hhStr);
  let mm = Number(mmStr);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return '';
  let total = hh * 60 + mm + addMin;
  if (total < 0) total = 0;
  if (total > 23 * 60 + 59) total = 23 * 60 + 59;
  const outH = String(Math.floor(total / 60)).padStart(2, '0');
  const outM = String(total % 60).padStart(2, '0');
  return `${outH}:${outM}`;
}

export default function LeadDetailsModal({
  open,
  loading,
  lead,
  error,
  onClose,
  onSaved,
}) {
  const leadId = useMemo(() => getAny(lead || {}, ['id', '_id', 'leadid', 'lead_id']), [lead]);

  // ---------- Form state (Left column) ----------
  const [form, setForm] = useState({
    name: '',
    mobile: '',
    email: '',
    status: '',
    agent: '',
    ass: '',
    bname: '',
    date: '',
    time: '',
    comment: '',
    tmzone: '',
    mpv: '',
  });

  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createDocError, setCreateDocError] = useState('');
  const [createDocOk, setCreateDocOk] = useState('');

  const [duplicating, setDuplicating] = useState(false);
  const [leadFlags, setLeadFlags] = useState({ is_reprogram: 0, is_duplicate: 0, source_lead_id: null });

  const [saveError, setSaveError] = useState('');
  const [saveOk, setSaveOk] = useState('');

  // ---------- Notes / Specialist Comment (Right column) ----------
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesError, setNotesError] = useState('');

  const [specialistComment, setSpecialistComment] = useState('');
  const [commentSaving, setCommentSaving] = useState(false);
  const [commentError, setCommentError] = useState('');
  const [commentOk, setCommentOk] = useState('');

  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [agentsError, setAgentsError] = useState('');

  const dispatch = useDispatch();
  const user = useSelector(selectUser);

  const persistedAuth = getUserFromPersist();
  const role = String(user?.role || persistedAuth?.role || '').toLowerCase();
  const isAdmin = role === 'admin';

  function getUserFromPersist() {
    const keysToTry = ['persist:root', 'persistroot'];

    for (const key of keysToTry) {
      try {
        const raw = window.localStorage.getItem(key);
        if (!raw) continue;

        const root = JSON.parse(raw);
        const authObj =
          typeof root?.auth === 'string'
            ? JSON.parse(root.auth)
            : root?.auth;

        if (authObj?.user) return authObj.user;
      } catch {
        // ignore
      }
    }
    return null;
  }

  // Initialize form whenever a new lead arrives
  useEffect(() => {
    if (!lead) return;
    const init = {
      name: getAny(lead, ['name']),
      mobile: getAny(lead, ['mobile', 'phone']),
      email: getAny(lead, ['email']),
      status: getAny(lead, ['status']),
      agent: getAny(lead, ['agent', 'aid']),
      bname: getAny(lead, ['bname', 'business', 'company']),
      date: toDateYYYYMMDD(getAny(lead, ['calldate', 'call_date', 'callDate'])),
      time: applyAmPmLogic(toHHMM(getAny(lead, ['calltime', 'call_time', 'callTime']))),
      comment: getAny(lead, ['comment', 'agent_comment', 'notes']),
      tmzone: getAny(lead, ['tmzone', 'timezone']),
      ass: getAny(lead, ['ass']),
      mpv: getAny(lead, ['value']),
    };
    setForm(init);
    setSaveError('');
    setSaveOk('');
    setSpecialistComment('');
    setCommentError('');
    setCommentOk('');
  }, [lead]);

  // Fetch lead flags (is_reprogram, is_duplicate) directly from lead record
  useEffect(() => {
    let ignore = false;
    async function run() {
      if (!open || !leadId) return;
      try {
        const resp = await API.get(`/auth/lead/${leadId}`);
        const ld = resp?.data || {};
        if (!ignore) setLeadFlags({
          is_reprogram: Number(ld.is_reprogram) || 0,
          is_duplicate: Number(ld.is_duplicate) || 0,
          source_lead_id: ld.source_lead_id ?? null,
        });
      } catch {
        // non-critical — badges just won't show
      }
    }
    run();
    return () => { ignore = true; };
  }, [open, leadId]);

  // Fetch notes for this lead
  useEffect(() => {
    let ignore = false;
    async function run() {
      if (!open || !leadId) return;
      setNotesLoading(true);
      setNotesError('');
      try {
        const resp = await API.get(`/auth/notes/${leadId}`);
        const data = resp?.data;
        let arr = [];
        if (Array.isArray(data)) arr = data;
        else if (Array.isArray(data?.notes)) arr = data.notes;
        else if (data && typeof data === 'object') arr = [data];
        if (!ignore) setNotes(arr);
      } catch (e) {
        console.error(e);
        if (!ignore) setNotesError('Could not load notes.');
      } finally {
        if (!ignore) setNotesLoading(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [open, leadId]);

  useEffect(() => {
    if (!isAdmin || !open) return;

    let ignore = false;

    async function fetchAgents() {
      setAgentsLoading(true);
      setAgentsError('');
      try {
        const resp = await API.get('/auth/users');
        const rows = Array.isArray(resp?.data) ? resp.data : [];

        const onlyAgents = rows.filter(
          (u) => String(u.role).toLowerCase() === 'agent'
        );

        if (!ignore) setAgents(onlyAgents);
      } catch (e) {
        console.error(e);
        if (!ignore) setAgentsError('Failed to load agents');
      } finally {
        if (!ignore) setAgentsLoading(false);
      }
    }

    fetchAgents();
    return () => {
      ignore = true;
    };
  }, [isAdmin, open]);

  const onChange = (field) => (e) => {
    const val = e.target.value;
    setForm((f) => ({ ...f, [field]: val }));
  };

  /** Insert a specialist note into scomments table */
  const insertSpecialistNote = async (text) => {
    const trimmed = String(text || '').trim();
    if (!trimmed || !leadId) return;

    setCommentSaving(true);
    setCommentError('');
    setCommentOk('');

    try {

      const userName = getUsernameFromReduxOrPersist();

      const payload = {
        leadid: leadId,
        scomment: trimmed,
        UpdatedBy: userName || 'Unknown',
      };


      await API.post('/auth/insertNotes', payload);

      // optimistic prepend
      setNotes((prev) => [
        {
          id: `temp-${Date.now()}-${Math.random().toString(16).slice(2)}`,
          scomment: trimmed,
          UpdatedBy: payload.UpdatedBy,
          last_update: new Date().toISOString(),
        },
        ...prev,
      ]);

      setCommentOk('Comment saved.');
      setTimeout(() => setCommentOk(''), 2000);
    } catch (e) {
      console.error(e);
      setCommentError('Could not save comment.');
      setTimeout(() => setCommentError(''), 2500);
    } finally {
      setCommentSaving(false);
    }
  };


  /** Validate ONLY that selected call DATE is today/future in EST (NO time validation) */
  const validateCallDateEST = () => {
    const dateStr = String(form.date || "").trim();

    // If user didn't select date at all -> fail (only when status requires it)
    if (!dateStr) return { ok: false, msg: "Please select Call Date (EST)." };

    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return { ok: false, msg: "Invalid Call Date format." };
    }

    const { yyyy, mm, dd } = estNowParts();
    const todayEST = `${yyyy}-${mm}-${dd}`;

    if (dateStr < todayEST) {
      return { ok: false, msg: "Call Date must be today or future (EST)." };
    }

    return { ok: true };
  };


  const [deleting, setDeleting] = useState(false);

  const onDeleteLead = async () => {
    if (!leadId) return;
    const confirmed = window.confirm('Really delete this lead? This cannot be undone.');
    if (!confirmed) return;
    try {
      setDeleting(true);
      await API.delete(`/auth/leads/${leadId}`);
      toast.success('Lead deleted.');
      onSaved?.({ __deleted: true, id: leadId });
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to delete lead.';
      toast.error(`Delete failed: ${msg}`);
    } finally {
      setDeleting(false);
    }
  };

  const onSave = async () => {
    if (!leadId) return;


    // Validate Call Date/Time ONLY for callback or answering
    const needsCallValidation = ['callback', 'answering'].includes(
      String(form.status || '').toLowerCase()
    );

    if (needsCallValidation) {
      const v = validateCallDateEST();
      if (!v.ok) {
        setSaveError(v.msg || "Invalid call date.");
        setTimeout(() => setSaveError(""), 2500);
        return;
      }
    }




    setSaving(true);
    setSaveError('');
    setSaveOk('');

    try {
      // ensure we know user's email
      let current = user;
      if (!current?.email) {
        const { data } = await API.get('/auth/me');
        if (data?.user) {
          current = data.user;
          dispatch(setUser(current));
        }
      }

      if (!current?.email) {
        setSaveError('You must be logged in to send status email.');
        return;
      }

      const payload = {
        leadid: leadId,
        name: form.name,
        mobile: form.mobile,
        email: form.email,
        status: form.status,
        agent: form.agent,
        ass: form.ass,
        bname: form.bname,
        calldate: form.date || null,
        calltime: form.time || null, // no time normalization/validation
        comment: form.comment || null,
        tmzone: form.tmzone || null,
        mpv: form.mpv || null,

        from: current.email,
      };

      // 2) Save lead fields
      // LeadDetailsModal.jsx
      // const resp = await API.post(`/auth/updateLead/${leadId}`, payload);
      // const updatedFromServer = resp?.data;

      // onSaved?.(updatedFromServer || {
      //   ...lead,
      //   ...payload,
      //   id: leadId,
      // });




      // 3) Save specialist comment (if typed) on Save
      // const typed = String(specialistComment || '').trim();
      // if (typed) {
      //   await insertSpecialistNote(typed);
      //   setSpecialistComment('');
      // }


      // 2) Save lead fields
      const resp = await API.post(`/auth/updateLead/${leadId}`, payload);
      const updatedFromServer = resp?.data;

      // ── Email notification (status change to answering / notintrested) ───
      const emailSent = updatedFromServer?.emailSent;
      if (emailSent === false && updatedFromServer?.error) {
        toast.error(`Email failed: ${updatedFromServer.error}`, { autoClose: 6000 });
      } else if (emailSent === false && payload.status &&
        ['answering', 'notintrested', 'not_interested', 'notinterested'].includes(
          String(payload.status).toLowerCase()
        )) {
        toast.error('Status email failed to send. Check Google connection.', { autoClose: 6000 });
      }

      let merged = updatedFromServer || { ...lead, ...payload, id: leadId };

      // optional: keep mpv consistent in FE object (backend returns `value`)
      if (payload.mpv != null && merged.value == null) {
        merged = { ...merged, value: payload.mpv };
      }

      // ✅ update parent immediately for status/date/time/comment fields
      onSaved?.(merged);

      // 3) Save specialist comment (if typed) on Save
      const typed = String(specialistComment || '').trim();
      if (typed) {
        await insertSpecialistNote(typed);
        setSpecialistComment('');

        // ✅ IMPORTANT: Hotlead table shows scomment, so update parent too
        onSaved?.({ ...merged, scomment: typed });
      }



      // 4) If status is callback -> auto comment in EST
      // const st = String(form.status || '').trim().toLowerCase();
      // if (st === 'callback') {
      //   await insertSpecialistNote(`Call back set at ${estNowPretty()}`);
      // }

      setSaveOk('Saved.');
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.error || e?.response?.data?.detail || 'Save failed. Please try again.';
      setSaveError(msg);
      if (e?.response?.data?.error === 'Google account not connected for this user.') {
        toast.error('Email failed: Google account not connected.', { autoClose: 6000 });
      } else if (e?.response?.status >= 400) {
        toast.error(`Save failed: ${msg}`, { autoClose: 6000 });
      }
    } finally {
      setSaving(false);
      setTimeout(() => setSaveOk(''), 2500);
    }
  };

  function getUsername() {
    const fromToken = getUserFromToken()?.name || '';
    if (fromToken) return fromToken;

    const keysToTry = ['persistroot', 'persist:root'];
    for (const key of keysToTry) {
      try {
        const raw = window.localStorage.getItem(key);
        if (!raw) continue;

        const root = JSON.parse(raw);
        const authObj = typeof root?.auth === 'string' ? JSON.parse(root.auth) : root?.auth;

        const username =
          authObj?.user?.username ||
          authObj?.user?.name ||
          authObj?.username ||
          '';

        if (username) return String(username);
      } catch {
        // ignore
      }
    }
    return '';
  }


  function getUsernameFromReduxOrPersist() {
    // 1) redux
    const fromRedux =
      user?.username ||
      user?.name ||
      user?.email ||
      '';
    if (fromRedux) return String(fromRedux);

    // 2) persist:root
    const keysToTry = ['persist:root', 'persistroot'];
    for (const key of keysToTry) {
      try {
        const raw = window.localStorage.getItem(key);
        if (!raw) continue;

        const root = JSON.parse(raw);
        const authObj =
          typeof root?.auth === 'string'
            ? JSON.parse(root.auth)
            : root?.auth;

        const fromPersist =
          authObj?.user?.username ||
          authObj?.user?.name ||
          authObj?.user?.email ||
          '';

        if (fromPersist) return String(fromPersist);
      } catch {
        // ignore
      }
    }

    return '';
  }


  const onCreateDocument = async () => {
    if (!leadId) return;

    setCreating(true);
    setCreateDocError('');
    setCreateDocOk('');

    try {
      // const username = getUsername();
      const username = getUsernameFromReduxOrPersist();
      const payload = {
        lead_id: leadId,
        name: form.name || '',
        bname: form.bname || '',
        email: form.email || '',
        phone: form.mobile || '',
        agent: form.agent || '',
        username: username,
      };

      await API.post('/auth/mpa', payload);
      setCreateDocOk('Document created (MPA saved).');
    } catch (e) {
      console.error(e);
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        'Create document failed.';
      setCreateDocError(msg);
    } finally {
      setCreating(false);
      setTimeout(() => setCreateDocOk(''), 2500);
      setTimeout(() => setCreateDocError(''), 4000);
    }
  };

  const onDuplicateLead = async () => {
    if (!leadId) return;
    const confirmed = window.confirm(
      'This will create a duplicate lead with a new ID and all the same details. No email or SMS will be sent. Continue?'
    );
    if (!confirmed) return;

    setDuplicating(true);
    try {
      const resp = await API.post('/auth/duplicateLead', { lead_id: leadId });
      const newLead = resp?.data;
      toast.success(`Duplicate lead created — new Lead ID: ${newLead?.id}`);
      onSaved?.(newLead);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to duplicate lead.';
      toast.error(`Duplicate failed: ${msg}`);
    } finally {
      setDuplicating(false);
    }
  };

  // Build Status dropdown
  const defaultStatuses = ['callback', 'answering', 'notintrested', 'sold', 'approve'];

  // Build Time Zone dropdown
  const defaultTimeZones = ['est', 'cst', 'mst', 'pst', 'hst'];
  const statusOptions = Array.from(new Set([...(form.status ? [form.status] : []), ...defaultStatuses]));
  const timeZoneOptions = Array.from(new Set([...(form.tmzone ? [form.tmzone] : []), ...defaultTimeZones]));

  const ASSIGNEE_LABELS = {
    0: 'Not assigned',
    1: 'Alan',
    2: 'Chris',
    3: 'david',
    4: 'akshay',
    5: 'mike',
  };

  function assigneeLabel(v) {
    if (v === null || typeof v === 'undefined' || v === '') return '—';
    const n = Number(v);
    if (!Number.isFinite(n)) return 'Others';
    return ASSIGNEE_LABELS.hasOwnProperty(n) ? ASSIGNEE_LABELS[n] : 'Others';
  }

  // --- Date/time min constraints in EST ---
  const minDateEST = estTodayYYYYMMDD();
  const todayEST = minDateEST;

  if (!open) return null;

  return (
    <div className="ldm-backdrop" role="presentation">
      <div
        className="ldm-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="ldm-header">
          <h3>
            Lead Details <br />
            <sub className='ldm-header-ass'>
              Assigned To:{' '}
              {isAdmin ? (
                <select
                  className="ldm-header-ass-select"
                  value={form.ass ?? 0}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    setForm((f) => ({ ...f, ass: Number.isFinite(n) ? n : 0 }));
                  }}
                >
                  <option value={0}>Not assigned</option>
                  <option value={1}>Alan</option>
                  <option value={2}>Chris</option>
                  <option value={3}>david</option>
                  <option value={4}>akshay</option>
                  <option value={5}>mike</option>
                  <option value={6}>cyrus</option>
                </select>
              ) : (
                assigneeLabel(form.ass)
              )}
            </sub>
          </h3>

          <div className="ldm-actions">
            {createDocError && <span className="ldm-banner ldm-banner-error">{createDocError}</span>}
            {createDocOk && <span className="ldm-banner ldm-banner-ok">{createDocOk}</span>}

            <button
              className="ldm-btn ldm-btn-danger"
              onClick={onDeleteLead}
              disabled={deleting || loading || !!error || !leadId}
            >
              {deleting ? 'Deleting…' : 'Delete Lead'}
            </button>

            <button
              className="ldm-btn"
              onClick={onDuplicateLead}
              disabled={duplicating || loading || !!error || !leadId}
              title="Create a copy of this lead with a new Lead ID"
            >
              {duplicating ? 'Duplicating…' : 'Duplicate Lead'}
            </button>

            <button
              className="ldm-btn"
              onClick={onCreateDocument}
              disabled={creating || loading || !!error || !leadId}
            >
              {creating ? 'Creating…' : 'Create Document'}
            </button>

            {saveError && <span className="ldm-banner ldm-banner-error">{saveError}</span>}
            {saveOk && <span className="ldm-banner ldm-banner-ok">{saveOk}</span>}

            <button className="ldm-btn ldm-btn-primary" onClick={onSave} disabled={saving || loading || !!error || commentSaving}>
              {(saving || commentSaving) ? 'Saving…' : 'Save'}
            </button>

            <button className="ldm-close" onClick={onClose} aria-label="Close">✕</button>
          </div>
        </div>

        {(loading && <div className="ldm-body">Loading…</div>)}
        {(error && !loading && <div className="ldm-body ldm-error">{error}</div>)}

        {!loading && !error && (
          <div className="ldm-body">
            <div className="ldm-two-col">
              {/* LEFT: Lead details */}
              <div className="ldm-col ldm-col-left">
                <div className="ldm-lead-head">
                  <div className="ldm-avatar">{initialFromName(form.name)}</div>
                  <div className="ldm-head-main">
                    <div className="ldm-lead-name">{form.name || '—'}</div>
                    <div className="ldm-chip-row">
                      {form.status ? <span className="ldm-chip ldm-chip-hot">{form.status}</span> : null}
                      {leadId ? <span className="ldm-chip">{leadId}</span> : null}
                      {form.agent ? <span className="ldm-chip">{form.agent}</span> : null}
                      {Number(leadFlags.is_duplicate) === 1 && (
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            background: '#ede9fe',
                            color: '#5b21b6',
                            border: '1px solid #c4b5fd',
                            borderRadius: 999,
                            fontSize: 11,
                            fontWeight: 700,
                            padding: '2px 10px',
                            letterSpacing: 0.3,
                          }}
                          title={`Duplicated from Lead #${leadFlags.source_lead_id}`}
                        >
                          ⧉ DUPLICATE{leadFlags.source_lead_id ? ` of #${leadFlags.source_lead_id}` : ''}
                        </span>
                      )}
                      {Number(leadFlags.is_reprogram) === 1 && (
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            background: '#fef3c7',
                            color: '#92400e',
                            border: '1px solid #fcd34d',
                            borderRadius: 999,
                            fontSize: 11,
                            fontWeight: 700,
                            padding: '2px 10px',
                            letterSpacing: 0.3,
                          }}
                          title="This is a reprogram lead — equipment replacement request"
                        >
                          ↺ REPROGRAM
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="ldm-cards">
                  <div className="ldm-card">
                    <div className="ldm-card-label">Call Date</div>
                    <div className="ldm-card-value">{fmtDatePretty(form.date)}</div>
                  </div>
                  <div className="ldm-card">
                    <div className="ldm-card-label">Call Time</div>
                    <div className="ldm-card-value">
                      {form.time ? (() => {
                        const [hhStr, mmStr] = form.time.split(':');
                        const hh = parseInt(hhStr, 10);
                        const mm = mmStr || '00';
                        let suffix;
                        if (hh >= 12) {
                          suffix = 'PM';
                        } else if (hh >= 10) {
                          suffix = 'AM';
                        } else {
                          suffix = 'PM';
                        }
                        const displayHH = hh === 0 ? 12 : hh > 12 ? hh - 12 : hh;
                        return `${displayHH}:${mm} ${suffix}`;
                      })() : '—'}
                    </div>
                  </div>
                </div>

                <div className="ldm-tabs">
                  <button className="active" type="button">Basic Info</button>
                  <button className="ghost" type="button" disabled>Company Info</button>
                  <button className="ghost" type="button" disabled>Deal Info</button>
                </div>

                <div className="ldm-grid">
                  <div className="ldm-field">
                    <label>Name</label>
                    <input value={form.name} onChange={onChange('name')} placeholder="Full name" />
                  </div>

                  <div className="ldm-field">
                    <label>Mobile</label>
                    <input value={form.mobile} placeholder="Mobile" readOnly aria-readonly="true" />
                  </div>

                  <div className="ldm-field">
                    <label>Email</label>
                    <input type="email" value={form.email} onChange={onChange('email')} placeholder="Email" />
                  </div>

                  <div className="ldm-field">
                    <label>Status</label>
                    <select value={form.status || ''} onChange={onChange('status')}>
                      <option value="" disabled>Select status</option>
                      {statusOptions.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>

                  <div className="ldm-field">
                    <label>Company</label>
                    <input value={form.bname || ''} onChange={onChange('bname')} placeholder="Company" />
                  </div>

                  <div className="ldm-field">
                    <label>Agent</label>
                    {isAdmin ? (
                      <select
                        value={form.agent || ''}
                        onChange={(e) => setForm((f) => ({ ...f, agent: e.target.value }))}
                        disabled={agentsLoading}
                      >
                        <option value="">Select agent</option>
                        {agents.map((a) => (
                          <option key={a.id} value={a.username}>
                            {a.username}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input value={form.agent || ''} readOnly aria-readonly="true" />
                    )}

                    {agentsError && <small style={{ color: 'red' }}>{agentsError}</small>}
                  </div>

                  <div className="ldm-field">
                    <label>Time Zone</label>
                    <select value={form.tmzone || ''} onChange={onChange('tmzone')}>
                      <option value="" disabled>Select time zone</option>
                      {timeZoneOptions.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>

                  <div className="ldm-field">
                    <label>Call Date </label>
                    <input
                      type="date"
                      value={form.date}
                      onChange={onChange('date')}
                      min={minDateEST}
                    />
                    {/* <small style={{ opacity: 0.75 }}>
                      Past dates disabled (based on EST).
                    </small> */}
                  </div>

                  <div className="ldm-field">
                    <label>Call Time</label>
                    <input
                      type="time"
                      value={form.time}
                      onChange={onChange('time')}
                    />
                    {/* <small style={{ opacity: 0.75 }}>
                      Time is optional (no EST future-time validation).
                    </small> */}

                  </div>
                  <div className="ldm-field">
                    <label>Monthly Processing</label>
                    <input value={form.mpv || ''} onChange={onChange('mpv')} placeholder="Monthly Processing" />
                  </div>

                  <div className="ldm-field ldm-field-full">
                    <label>Agent Comment</label>
                    <textarea value={form.comment || ''} onChange={onChange('comment')} placeholder="Agent/general notes" />
                  </div>
                </div>
              </div>

              {/* RIGHT: Specialist comments */}
              <div className="ldm-col ldm-col-right">
                <div className="ldm-field">
                  <label>Specialist Comment</label>
                  <textarea
                    value={specialistComment}
                    onChange={(e) => setSpecialistComment(e.target.value)}
                    placeholder="Type here… (will be saved when you click Save)"
                  />
                  <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
                    {commentError && <span className="ldm-banner ldm-banner-error">{commentError}</span>}
                    {commentOk && <span className="ldm-banner ldm-banner-ok">{commentOk}</span>}
                    <small style={{ opacity: 0.75 }}>
                      {/* No separate button — this saves with <b>Save</b>. If status = <b>callback</b>, an automatic comment is added. */}
                    </small>
                  </div>
                </div>

                <div className="ldm-section-title">Comments</div>

                {notesLoading && <div className="ldm-notes-loading">Loading comments…</div>}
                {notesError && <div className="ldm-notes-error">{notesError}</div>}

                {!notesLoading && !notesError && notes.length === 0 && (
                  <div className="ldm-notes-empty">No comments yet.</div>
                )}

                {!notesLoading && !notesError && notes.length > 0 && (
                  <div className="ldm-notes">
                    {notes.map((n, idx) => {
                      const who = n?.UpdatedBy ?? '—';
                      const text = n?.scomment ?? '—';
                      const when = n?.last_update ? fmtDateTime(n.last_update) : '';

                      return (
                        <div className="ldm-note" key={n.id || n._id || idx}>
                          <div className="ldm-note-meta">
                            <span className="ldm-note-who">{String(who)}</span>
                            {when ? <span className="ldm-note-when">• {when}</span> : null}
                          </div>
                          <div className="ldm-note-text">{String(text)}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

            </div>
          </div>
        )}
      </div>
    </div>
  );
}
